import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const basicCustomEventCode = `// 1. Basic Custom Event Definition
const event = new CustomEvent('app:moduleReady', {
    detail: { timestamp: Date.now() }
});

// 2. Dispatching from a target
const statusNode = document.getElementById('status-api');
statusNode.dispatchEvent(event);

// 3. Centralized Event Management
document.addEventListener('app:moduleReady', (e) => {
    console.log(\`Module initialized at: \${e.detail.timestamp}\`);
});`;

const observationPatternCode = `// --- Implementation of the Observer Pattern ---
class ArchitectureSubject {
    constructor() {
        this.observers = new Set();
    }

    subscribe(fn) {
        this.observers.add(fn);
        return () => this.observers.delete(fn); // Returns teardown logic
    }

    notify(data) {
        this.observers.forEach(fn => fn(data));
    }
}

// System usage
const authStream = new ArchitectureSubject();
const cleanupSidebar = authStream.subscribe(user => updateSidebar(user));
const cleanupHeader = authStream.subscribe(user => updateHeader(user));

// Trigger update
authStream.notify({ id: 101, name: 'Architect' });`;

const eventEmitterCode = `// --- Professional Event Emitter (Node.js style) ---
class EventEmitter {
    constructor() {
        this.registry = {};
    }

    on(event, handler) {
        if (!this.registry[event]) this.registry[event] = [];
        this.registry[event].push(handler);
    }

    emit(event, ...payload) {
        if (!this.registry[event]) return;
        this.registry[event].forEach(handler => handler(...payload));
    }
}

const bus = new EventEmitter();
bus.on('data:sync', (id) => console.log(\`Syncing record: \${id}\`));
bus.emit('data:sync', 'db-v8-92');`;

const JSCustomEvents = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Event-Driven Architecture</div>
                <h1>Custom Events & Event Patterns</h1>
                <p className="hero-lead">
                    In complex software systems, components must remain loosely coupled.
                    Master the art of creating custom communication protocols using
                    the **Observer Pattern**, **Event Emitters**, and the native
                    **CustomEvent** API.
                </p>
            </div>

            <div className="content-section">
                <h2>The Power of Decoupling</h2>
                <p>
                    Communication is the hardest part of scaling a codebase.
                    If your Login component must directly call the Header, Sidebar,
                    and Analytics components, your code becomes "brittle." By using
                    <strong>Events</strong>, the Login component simply announces,
                    "Someone logged in," and ignores who is listening. This is the
                    foundation of modular engineering.
                </p>
                <ProTip><ul>
                        <li><strong>Isolation:</strong> Test components without instantiating the entire UI tree.</li>
                        <li><strong>Scalability:</strong> Add new features (like a Notification toast) by just adding a new listener.</li>
                        <li><strong>Reliability:</strong> Failures in one listener don't necessarily break the event dispatcher.</li>
                    </ul></ProTip>
            </div>

            <div className="content-section">
                <h2>Native CustomEvent API</h2>
                <p>
                    Modern browsers support the <code>CustomEvent</code> constructor, which
                    allows you to define proprietary event types. These events travel
                    through the same DOM pipeline as standard clicks or keyboard inputs,
                    complete with bubbling and cancellation mechanisms.
                </p>
                <CodeBlock code={basicCustomEventCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>The Observer Pattern</h2>
                <p>
                    The **Observer Pattern** is a structural design pattern where an
                    object (Subject) maintains a list of dependents (Observers). This
                    is the "engine" behind React's state updates and most modern
                    reactive frameworks. It allows for a **One-to-Many** communication
                    stream.
                </p>
                <CodeBlock code={observationPatternCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Event Emitters & Pub/Sub</h2>
                <p>
                    While the native API is tethered to the DOM, the <strong>Event Emitter</strong>
                    pattern is environment-agnostic. It is heavily utilized in
                    Node.js for server-side logic and in front-end "Event Buses" for
                    cross-component communication that doesn't follow the DOM tree.
                </p>
                <CodeBlock code={eventEmitterCode} language="javascript" />
            </div>

            <ProTip><p>
                    The primary cause of **Memory Leaks** in event-driven apps is
                    dangling listeners. Always ensure that listeners are removed when
                    a component "unmounts" or becomes irrelevant. If you subscribe in
                    a constructor, you MUST unsubscribe in the destructor logic.
                </p></ProTip>

            <KeyFacts><ul>
                    <li>**Namespacing:** Use colon-notation (e.g., <code>auth:login</code>) for clarity.</li>
                    <li>**Payloads:** Use the <code>detail</code> property for custom event data.</li>
                    <li>**Teardowns:** Return a function from your subscribers to simplify cleanup.</li>
                    <li>**Bubbling:** Only enable <code>bubbles: true</code> if the event needs to traverse the DOM.</li>
                    <li> **Circular Logic:** Avoid emitting events from within their own handlers.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSCustomEvents;


